import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {EnumParameter} from '../../../editors/commons/parameterModel';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {MoveAction, RuleAction} from '../../ruleActionModel';
import {RuleActionDefinition} from '../../ruleParameterModel';
import {RuleActionIcon} from '../ruleActionIcon';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {renderGenericRuleParameters} from './ruleDefaultActionConditionCard';

/*
 * Props.
 */

interface RuleMoveToInboxActionCardProps {
  action: MoveAction;
  actionDefinition: RuleActionDefinition | undefined;
  isDisabled?: boolean;
  interactionId?: string;
  errorMessage?: ReactNode;
  onUpdate: HandlerOf<RuleAction>;
  onDelete?: HandlerOf<RuleAction>;
}

/*
 * Styles.
 */

const MiddleConditionLabel = styled.span`
  margin: 0 5px;
`;

const ParametersWrapper = styled.div`
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const RuleMoveToInboxActionCard: FC<RuleMoveToInboxActionCardProps> = (props) => {
  const {action, actionDefinition, isDisabled, interactionId, errorMessage, onUpdate, onDelete} = props;

  const moveTypeEnum = actionDefinition?.parameters?.moveType as EnumParameter | undefined;

  // This should not happen, but safety check.
  const errorLabel = <Trans id="manifest-action-not-found">Action {action.type} not found</Trans>;
  if (!actionDefinition || !moveTypeEnum) {
    return <RuleMultilineParameterCard icon={<RuleActionIcon id={action.type} />} title={errorLabel} />;
  }

  const onCancel = onDelete && !isDisabled ? () => onDelete(action) : undefined;

  const {
    parameters: [inboxParameter, restrictionParameter],
    collapsableParameters,
  } = renderGenericRuleParameters(
    action,
    actionDefinition,
    RuleDefaultCardTypesEnum.ACTION,
    onUpdate,
    actionDefinition.parameters ?? {},
    isDisabled,
  );

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>Move to</MultilineRuleParameterCardDefaultTitleStyled>
      <ParametersWrapper>
        {inboxParameter}
        <MiddleConditionLabel>and</MiddleConditionLabel>
        {restrictionParameter}
      </ParametersWrapper>
    </>
  );

  return (
    <RuleMultilineParameterCard
      interactionId={interactionId}
      icon={<RuleActionIcon id={action.type} />}
      title={title}
      errorMessage={errorMessage}
      onCancel={onCancel}
      collapsableParameters={collapsableParameters}
    />
  );
};
